import {push} from 'connected-react-router';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AsyncResult} from 'Core/models/Async';
import {emailValidationRegex, ValidEmail} from 'Core/models/Email';
import {NonEmptyString} from 'Core/models/EmptyString';
import {ValidPhoneNumber} from 'Core/models/PhoneNumber';
import {RoleId} from 'Core/models/Role';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {ReducerState} from 'rootReducer';
import {Scope} from 'Shared/constants/Scope';
import {UserUploadFileTypeId} from 'Shared/constants/UserUploadFileType';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {postMultipart} from 'Shared/utilities/fetchHelpers';
import {apiCallGenerator, getValidationSaga} from 'Shared/utilities/sagaApiGenerators';
import {AgentNumberDto, AgentUserTypeDto, UserEntryDto} from 'Users/api/api';
import userApi from 'Users/api/userApi';
import userEntryApi from 'Users/api/userEntryApi';
import {uploadUsers, UserAdministrationActionType} from 'Users/ducks/userAdministration';
import {
  addUser,
  changeEmail,
  changePhoneNumber,
  changeUserName,
  clearState,
  showError,
  updateUser,
  UserEntryActionType,
} from 'Users/ducks/userEntry';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {SupportAgentPermissions} from 'Users/models/User/Agent/SupportAgentPermissions';
import {ExistingTaxIdentificationNumber} from 'Users/models/User/Agent/TaxIdentificationNumber';
import {matchValidAgentUserType, ValidAgentUserType} from 'Users/models/User/AgentUserType';
import {isExternalBasicInformation} from 'Users/models/User/BasicInformation';
import {ExclusivityStatus} from 'Users/models/User/ExclusivityStatus';
import {ValidUser} from 'Users/models/User/User';
import {matchValidUserTypeByLineOfBusiness} from 'Users/models/User/UserType';
import {UserRouteConstants} from 'Users/Routes';
import {extractApplicationIdsFromScopeSaga} from './applicationIdSaga';

function* validateEmailSaga() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_EMAIL,
    getValidationSaga(ValidEmail.create, changeEmail, userApi.validateEmail, value => emailValidationRegex.test(value))
  );
}

function* validateUsernameSaga() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_USERNAME,
    getValidationSaga(NonEmptyString.create, changeUserName, userApi.validateAllstateUsername)
  );
}

function* validatePhoneNumberSaga() {
  yield takeLatest(
    UserEntryActionType.VALIDATE_PHONE,
    getValidationSaga(ValidPhoneNumber.create, changePhoneNumber, userApi.validatePhoneNumber)
  );
}

const mapAgentSupportRoles = (permission: SupportAgentPermissions) => {
  const roles = [];

  if (permission.canViewAccounts) {
    roles.push({id: RoleId.AgencySupportWithAccounts});
  }
  if (permission.canViewPolicies) {
    roles.push({id: RoleId.AgencySupportWithPolicies});
  }
  if (permission.canViewCommissionsAnd1099s) {
    roles.push({id: RoleId.AgencySupportWithCommissionsAnd1099s});
  }
  if (permission.canViewPolicyServiceRequest) {
    roles.push({id: RoleId.AgencySupportWithPolicyServiceRequest});
  }
  if (permission.canViewCorrespondence) {
    roles.push({id: RoleId.AgencySupportWithCorrespondence});
  }
  if (permission.canViewClaims) {
    roles.push({id: RoleId.AgencySupportWithClaims});
  }
  if (permission.canViewPendingUnderwritingRequirements) {
    roles.push({id: RoleId.AgencySupportWithUnderwritingRequirements});
  }

  return roles;
};

const mapProducerRoles = (isAgencyAdministrator: boolean) => {
  const roles = [{id: RoleId.Producer}];

  if (isAgencyAdministrator) {
    roles.push({id: RoleId.AgencyAdministrator});
  }

  return roles;
};

const mapAgentUserToDto = (agentUserType: ValidAgentUserType): AgentUserTypeDto =>
  matchValidAgentUserType<AgentUserTypeDto>(agentUserType.value, {
    VIP: vipUserType => ({
      roles: vipUserType.isExternalAdministrator
        ? [{id: RoleId.VIP}, {id: RoleId.AllstateExternalAdministrator}]
        : [{id: RoleId.VIP}],
      isAhl: vipUserType.hasAhlAccess,
      isNewYork: vipUserType.hasNewYorkAccess,
    }),
    IAM: iamUserType => ({
      roles: iamUserType.isExternalAdministrator
        ? [{id: RoleId.IAM}, {id: RoleId.AllstateExternalAdministrator}]
        : [{id: RoleId.IAM}],
      isAhl: iamUserType.hasAhlAccess,
      isNewYork: iamUserType.hasNewYorkAccess,
    }),
    Agent: agentUserType => ({
      agencyAssociations: agentUserType.agencyAssociations
        .map(aa => ({
          identifier: aa.taxId instanceof ExistingTaxIdentificationNumber ? undefined : aa.taxId.value,
          encryptedIdentifier:
            aa.taxId instanceof ExistingTaxIdentificationNumber ? aa.taxId.encryptedIdentifier : undefined,
          agentNumbers: aa.agentNumbers as AgentNumberDto[],
          roles: mapProducerRoles(aa.isAgencyAdministrator),
        }))
        .toArray(),
    }),
    AgentSupport: agentSupportUserType => ({
      agencyAssociations: agentSupportUserType.agencyAssociations
        .map(aa => ({
          identifier: aa.taxId instanceof ExistingTaxIdentificationNumber ? undefined : aa.taxId.value,
          encryptedIdentifier:
            aa.taxId instanceof ExistingTaxIdentificationNumber ? aa.taxId.encryptedIdentifier : undefined,
          agentNumbers: aa.agentNumbers as AgentNumberDto[],
          roles: mapAgentSupportRoles(aa.supportAgentPermissions),
        }))
        .toArray(),
    }),
  });

const mapUserToDto = (user: ValidUser, id?: string): UserEntryDto => {
  const userTypes = user.userTypes
    .map(ut =>
      matchValidUserTypeByLineOfBusiness(ut.value, {
        Agent: (agentUserType): any =>
          agentUserType.value.key.includes('IAM')
            ? {agentUser: mapAgentUserToDto(agentUserType), portalUser: mapAgentUserToDto(agentUserType)}
            : {agentUser: mapAgentUserToDto(agentUserType)},
        Messaging: messagingUserType => ({
          messagingUser: {roles: [{id: messagingUserType.value.messagingRole}]},
        }),
        Quoting: quotingUserType => ({
          quotingUser: {roles: [{id: quotingUserType.value.quotingRole}]},
        }),
      })
    )
    .reduce((acc, ut) => ({...acc, ...ut}), {});

  const {username, phoneNumber} =
    ((!!id || !isExternalBasicInformation(user.basicInformation)) && user.basicInformation) || {};
 
  return {
    id,
    firstName: user.basicInformation.firstName.value,
    lastName: user.basicInformation.lastName.value,
    userName: username && username.Data.right.value,
    email: user.basicInformation.email.Data.right.address,
    isDisabled: user.basicInformation.isDisabled,
    mobilePhone: phoneNumber && E.isRight(phoneNumber.Data) ? phoneNumber.Data.right.phoneNumber : undefined,
    exclusivityStatus:
      user.basicInformation.accountType === 'Internal'
        ? {id: ExclusivityStatus.Internal}
        : {id: ExclusivityStatus.External},
    ...userTypes,
  };
};

function* returnToUsersPage() {
  yield put(clearState());
  yield put(createSnackbar('User has been successfully saved'));
  yield put(push(UserRouteConstants.USERS));
}

function* addUserSaga() {
  yield takeLatest(UserEntryActionType.ADD_USER, function* addUserApi(action: ReturnType<typeof addUser>) {
    const {user} = action.payload;
    const userDto = mapUserToDto(action.payload.user);

    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );
    const encryptedIdentifier : String | undefined = O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined;

    const applicationIds : ReturnType<typeof extractApplicationIdsFromScopeSaga> = yield extractApplicationIdsFromScopeSaga(Scope.AddUser);
    // @ts-ignore
    const result: AsyncResult<unknown> = yield call(userEntryApi.addUser, userDto, applicationIds, encryptedIdentifier);

    if (E.isRight(result)) {
      yield returnToUsersPage();
    } else {
      yield put(showError(user));
    }
  });
}

function* updateUserSaga() {
  yield takeLatest(UserEntryActionType.UPDATE_USER, function* updateUserApi(action: ReturnType<typeof updateUser>) {
    const {user, userId} = action.payload;
    const userDto = mapUserToDto(user, userId);

    const selectedAgency: O.Option<AgentAgencyClaim> = yield select(
      (state: ReducerState) => state.user.profile.selectedAgency
    );
    const encryptedIdentifier : String | undefined = O.isSome(selectedAgency) ? selectedAgency.value.encryptedIdentifier : undefined;

    const applicationIds : ReturnType<typeof extractApplicationIdsFromScopeSaga> = yield extractApplicationIdsFromScopeSaga(Scope.UpdateUser);
    const result: AsyncResult<unknown> = yield call(
      // @ts-ignore
      userEntryApi.updateUser,
      userDto,
      applicationIds,
      encryptedIdentifier
    );

    if (E.isRight(result)) {
      yield returnToUsersPage();
    } else {
      const message = result.left.message;

      yield put(createSnackbar(message));
      yield put(showError(user));
    }
  });
}

function* bulkUploadUsersSaga() {
  const uploadFile = (action: ReturnType<typeof uploadUsers>) => {
    const {file, agencyFilters} = action.payload;

    if (action.payload.file.fileTypeId === UserUploadFileTypeId.IndependentAgent) {
      if (agencyFilters) {
        return postMultipart(
          `api/user/v1/upload/independent/agency/isnewyork/${agencyFilters.isNewYork}${addFilterParameters({
            ...agencyFilters,
          })}`,
          file.file
        );
      } else {
        return postMultipart(`api/user/v1/upload/independent`, file.file);
      }
    } else {
      return postMultipart(`api/user/v1/upload?userUploadFileTypeId=${file.fileTypeId}`, file.file);
    }
  };

  yield takeLatest(UserAdministrationActionType.BULK_UPLOAD_USER, apiCallGenerator(uploadFile));
}

export default function*() {
  yield all([
    validateEmailSaga(),
    validateUsernameSaga(),
    validatePhoneNumberSaga(),
    addUserSaga(),
    updateUserSaga(),
    bulkUploadUsersSaga(),
  ]);
}
